import type { EpicMobileApi } from "./api";

export interface SmsEndpointSelection { phoneNumber: string; threadId: string; subscriptionId: number }
export interface SmsSelection { phoneNumber: string; country: string; since: number; mode?: "legacy" | "selected" | "all";
  selections?: SmsEndpointSelection[]; databaseGeneration?: string; initialUpper?: number; resetGeneration?: boolean }
export interface SmsBackgroundStatus {
  enabled: boolean; consentId: string | null; scopeConsentId: string | null; databaseGeneration?: string | null;
  state: "idle" | "scheduled" | "syncing" | "retrying" | "failed" | "paused" | "needs_login" | "up_to_date" | "delayed" | "disabled";
  lastAttemptAt?: number | string | null; lastSuccessAt?: number | string | null; reasonCode?: string | null;
  pendingStop?: boolean;
}
export interface SmsLocal extends SmsSelection {
  installationId: string; installationSecret: string; account: string; consentId: string;
  enabled: boolean; permissionGranted: boolean; pendingStop: "" | "pause" | "revoke";
  background?: SmsBackgroundStatus;
}
export interface SmsRemote {
  ok: boolean; accountUsername: string; status: "active" | "paused" | "revoked" | "not_registered";
  consentId: string | null; revision: number; enabled: boolean; importedCount: number; lastSyncAt: string | null;
  sendEnabled?: boolean; conversationRef?: string | null; supportedConsentVersions?: string[]; consentVersion?: string;
  conversations?: Array<{ conversationRef: string; scopeRef: string }>;
  readiness?: { ready: boolean; reasonCode: string | null };
  supportedBackgroundSync?: boolean; backgroundSync?: SmsBackgroundStatus;
}
export interface SmsReplyPermit { ok: true; operationId: string; account: string; consentId: string; conversationRef: string; phoneNumber: string; text: string; expiresAt: number; scopeRef?: string; threadId?: string; subscriptionId?: number }
export interface SmsReplyResult { operationId?: string; status: "cancelled" | "pending" | "sent" | "failed" | "partial" | "unknown" | "not_sent" }
export interface SmsPage { messages: Array<{ deviceMessageId: string; address: string; direction: "inbound" | "outbound"; occurredAt: number; text: string; threadId?: string; subscriptionId?: number }>; pageId: string; hasMore: boolean; databaseGeneration?: string;
  page?: { pageId: string; runId: string; after: number; through: number; upper: number; until: number; kind: "history" | "incremental" | "repair"; hasMore: boolean; unavailable?: number } }
export interface SmsReader {
  status(): Promise<SmsLocal>;
  discover(input: { account: string; country: string; confirmed: boolean; after: number }): Promise<{ conversations: SmsEndpointSelection[]; after: number; hasMore: boolean; unavailable: number }>;
  bindAccount(input: { account: string; actorId?: string }): Promise<void>;
  backgroundConfigure(input: { account: string; consentId: string; databaseGeneration: string; backgroundConsentId: string; confirmed: boolean }): Promise<void>;
  backgroundStop(): Promise<void>;
  backgroundStopAcknowledged(input: { backgroundConsentId: string }): Promise<void>;
  backgroundStatus(): Promise<SmsBackgroundStatus>;
  backgroundSync(input: { rescan: boolean }): Promise<void>;
  approve(input: SmsSelection & { account: string; consentId: string; confirmed: boolean }): Promise<void>;
  activate(input: { consentId: string }): Promise<void>;
  begin(input: { rescan: boolean }): Promise<void>;
  readPage(): Promise<SmsPage>;
  acknowledge(input: { pageId: string }): Promise<void>;
  stop(input: { mode: "pause" | "revoke" }): Promise<void>;
  stopAcknowledged(input: { consentId: string; mode: string }): Promise<void>;
  cancel(): Promise<void>;
  permissionSettings(): Promise<void>;
  send(input: SmsReplyPermit): Promise<SmsReplyResult>;
  sendStatus(input: { operationId: string }): Promise<SmsReplyResult>;
}
export interface SmsSyncState {
  account: string; busy: boolean; syncing: boolean; enabled: boolean; remote: SmsRemote | null;
  selection: SmsSelection | null; pendingStop: boolean; message: string; error: boolean;
  reply: SmsReplyResult | null; discovered: SmsEndpointSelection[]; discoveryAfter: number; discoveryMore: boolean; discoveryUnavailable: number;
  background: SmsBackgroundStatus | null;
}

export function createSmsSyncController(api: Pick<EpicMobileApi, "request">, reader: SmsReader) {
  let state: SmsSyncState = { account: "", busy: false, syncing: false, enabled: false, remote: null, selection: null, pendingStop: false, message: "", error: false, reply: null, discovered: [], discoveryAfter: 0, discoveryMore: true, discoveryUnavailable: 0, background: null };
  let listener = (_state: SmsSyncState) => {};
  let accountIdentity = "";
  let generation = 0;
  let request: AbortController | null = null;
  let binding: Promise<void> = Promise.resolve();
  const update = (patch: Partial<SmsSyncState>) => { state = { ...state, ...patch }; listener(state); };
  const valid = (version: number) => {
    if (generation !== version || !state.account) throw Object.assign(new Error("The account or sync session changed."), { code: "SMS_SYNC_CANCELLED" });
  };
  async function post<T>(action: string, local: SmsLocal, body: object = {}, signal?: AbortSignal): Promise<T> {
    try {
      return await api.request<T>(`/phone-sms/${action}`, { method: "POST", signal, timeoutMs: 45000,
        body: JSON.stringify({ installationId: local.installationId, installationSecret: local.installationSecret,
          expectedAccount: state.account, ...body }) });
    } catch (error: any) {
      if (error?.status === 404) throw Object.assign(new Error("Phone SMS is not available on this server yet. The SMS server update is required."), { code: "SMS_SERVER_UNAVAILABLE" });
      throw error;
    }
  }
  async function stopRemoteBackground(local: SmsLocal, scopeConsentId: string, backgroundConsentId: string, version: number, signal?: AbortSignal) {
    const remote = await post<SmsRemote>("background-disable", local, { consentId: scopeConsentId, backgroundConsentId }, signal); valid(version);
    if (remote.ok !== true || remote.accountUsername !== state.account || remote.backgroundSync?.consentId !== backgroundConsentId
      || remote.backgroundSync.enabled !== false) throw new Error("Background sync is stopped locally, but the server stop was not confirmed. Recheck status.");
    const background = await reader.backgroundStatus(); valid(version);
    // Server approval can exist without native configuration having succeeded.
    // A stop acknowledgement belongs only to a matching saved native grant.
    if (background.consentId === backgroundConsentId && !background.enabled) {
      await reader.backgroundStopAcknowledged({ backgroundConsentId }); valid(version);
    }
    return remote;
  }
  async function reconcile(version: number, signal?: AbortSignal) {
    // A previous successful status must not authorize reads while readiness is unknown.
    update({ enabled: false, remote: null });
    await binding; valid(version);
    const local = await reader.status(); valid(version);
    if (local.account && local.account !== state.account) throw new Error("This phone's SMS sync belongs to another EPIC account. Switch back to manage or revoke it.");
    update({ background: local.background || null });
    let remote = await post<SmsRemote>("status", local, {}, signal); valid(version);
    if (remote.ok !== true || remote.accountUsername !== state.account) throw new Error("The SMS server status did not match this account. Recheck the connection.");
    if (local.pendingStop) {
      remote = { ...remote, ...await post<SmsRemote>(local.pendingStop, local, { consentId: remote.consentId }, signal) }; valid(version);
      await reader.stopAcknowledged({ consentId: local.consentId, mode: local.pendingStop }); valid(version);
      local.pendingStop = "";
    }
    if (local.background?.enabled && (!remote.supportedBackgroundSync || !remote.backgroundSync?.enabled
      || remote.backgroundSync.consentId !== local.background.consentId
      || remote.backgroundSync.scopeConsentId !== local.consentId
      || remote.backgroundSync.databaseGeneration !== local.databaseGeneration || remote.status !== "active")) {
      await reader.backgroundStop(); valid(version);
      local.background = await reader.backgroundStatus(); valid(version);
    }
    if (local.background?.pendingStop && local.background.consentId) {
      const stopped = local.background;
      const stoppedId = local.background.consentId;
      if (remote.backgroundSync?.consentId === stoppedId && remote.backgroundSync.enabled) {
        remote = await stopRemoteBackground(local, stopped.scopeConsentId || local.consentId, stoppedId, version, signal);
      } else {
        // The old grant is already inactive or a different explicit grant now
        // exists. Never apply a delayed stop to that replacement grant.
        await reader.backgroundStopAcknowledged({ backgroundConsentId: stoppedId }); valid(version);
      }
      local.background = await reader.backgroundStatus(); valid(version);
    }
    update({ remote, enabled: remote.enabled === true && local.enabled && local.permissionGranted && remote.status === "active" && local.consentId === remote.consentId,
      selection: local.phoneNumber || local.mode === "selected" || local.mode === "all" ? { phoneNumber: local.phoneNumber, country: local.country, since: local.since,
        mode: local.mode, selections: local.selections, databaseGeneration: local.databaseGeneration, initialUpper: local.initialUpper } : null,
      pendingStop: false, background: local.background || null,
      message: remote.enabled !== true ? readinessMessage(remote)
        : local.enabled && !local.permissionGranted ? "Android SMS permission is off. Approve the conversation again to allow access." : "" });
    return { local, remote };
  }
  async function operation(work: (version: number, signal: AbortSignal) => Promise<void>) {
    if (state.busy || !state.account) return;
    const version = generation;
    request = new AbortController();
    update({ busy: true, error: false, message: "" });
    try { await work(version, request.signal); }
    catch (error: any) {
      if (generation === version) update({ error: true, message: error?.message || "SMS sync could not complete. Your progress is saved only after the server accepts each page." });
    } finally {
      if (generation === version) { request = null; update({ busy: false, syncing: false }); }
    }
  }
  return {
    getState: () => state,
    subscribe(callback: typeof listener) { listener = callback; listener(state); },
    setAccount(account: string, actorId: string) {
      const normalized = account.toLowerCase();
      const identity = `${actorId}:${normalized}`;
      if (identity === accountIdentity) return;
      accountIdentity = identity; generation++; request?.abort(); request = null;
      binding = binding.catch(() => {}).then(() => reader.bindAccount({ account: normalized, actorId }));
      update({ account: normalized, busy: false, syncing: false, enabled: false, remote: null, selection: null, pendingStop: false, message: "", error: false, reply: null, discovered: [], discoveryAfter: 0, discoveryMore: true, discoveryUnavailable: 0, background: null });
    },
    resetDiscovery() {
      if (!state.busy) update({ discovered: [], discoveryAfter: 0, discoveryMore: true, discoveryUnavailable: 0, message: "" });
    },
    discover(country: string, confirmed: boolean, restart = false) {
      return operation(async (version, signal) => {
        if (!confirmed) throw new Error("Approve reading local SMS address and thread information before discovery.");
        const { local } = await reconcile(version, signal); valid(version);
        if (local.account && local.account !== state.account) throw new Error("Switch back to this phone's bound account before discovery.");
        let after = restart ? 0 : state.discoveryAfter;
        let found = restart ? [] : [...state.discovered];
        let unavailable = restart ? 0 : state.discoveryUnavailable;
        for (let pageNumber = 0; pageNumber < 20; pageNumber++) {
          const page = await reader.discover({ account: state.account, country, confirmed, after }); valid(version);
          const unique = new Map(found.map(item => [JSON.stringify(item), item]));
          for (const item of page.conversations) unique.set(JSON.stringify(item), item);
          found = [...unique.values()]; unavailable += page.unavailable; after = page.after;
          update({ discovered: found, discoveryAfter: after, discoveryMore: page.hasMore, discoveryUnavailable: unavailable,
            message: `${found.length} SMS conversations found on this phone. No message text has been read or uploaded.${unavailable ? ` ${unavailable} source records have unsupported addresses or unavailable direct-thread participant evidence.` : ""}` });
          if (!page.hasMore) return;
        }
        update({ message: `${found.length} SMS conversations found. Tap Discover to continue.` });
      });
    },
    refresh() { return operation(async (version, signal) => { await reconcile(version, signal); }); },
    setBackground(enabled: boolean, confirmed: boolean) {
      return operation(async (version, signal) => {
        if (enabled && !confirmed) throw new Error("Approve background SMS access separately before enabling it.");
        if (!enabled) {
          // Stop native work first, even if the server or login is unavailable.
          await binding; valid(version);
          const local = await reader.status(); valid(version);
          await reader.backgroundStop(); valid(version);
          let background = await reader.backgroundStatus(); valid(version);
          update({ background });
          if (background.consentId) await stopRemoteBackground(local, background.scopeConsentId || local.consentId, background.consentId, version, signal);
          background = await reader.backgroundStatus(); valid(version);
          update({ background });
          update({ message: "Background SMS sync is off. Your approved scope and manual sync remain available." });
          return;
        }
        const { local, remote } = await reconcile(version, signal); valid(version);
        if (!state.enabled) throw new Error("Enable an approved SMS scope and Android permission first.");
        if (!remote.supportedBackgroundSync) throw new Error("The server update for background SMS sync is required.");
        if (!["all", "selected"].includes(local.mode || "") || !local.databaseGeneration) throw new Error("Review and approve a direct-conversation scope before enabling background SMS sync. The existing phone-number scope cannot be widened automatically.");
        if (local.background?.enabled) return;
        if (remote.backgroundSync?.enabled && remote.backgroundSync.consentId) {
          await reader.backgroundStop(); valid(version);
          const stopped = await stopRemoteBackground(local, remote.backgroundSync.scopeConsentId || local.consentId, remote.backgroundSync.consentId, version, signal);
          remote.revision = stopped.revision;
          remote.backgroundSync = stopped.backgroundSync;
        }
        const backgroundConsentId = crypto.randomUUID();
        const authorization = await post<{ ok: boolean; accountUsername: string; backgroundSync: SmsBackgroundStatus }>("background-authorize", local,
          { consentId: local.consentId, databaseGeneration: local.databaseGeneration, backgroundConsentId, confirmed: true,
            previousBackgroundConsentId: remote.backgroundSync?.consentId || null, previousRevision: remote.revision }, signal); valid(version);
        const grant = authorization.backgroundSync;
        if (authorization.ok !== true || authorization.accountUsername !== state.account || grant?.enabled !== true
          || grant.consentId !== backgroundConsentId || grant.scopeConsentId !== local.consentId || grant.databaseGeneration !== local.databaseGeneration) {
          throw new Error("Background approval did not match this phone, account, and SMS scope.");
        }
        try {
          await reader.backgroundConfigure({ account: state.account, consentId: local.consentId, databaseGeneration: local.databaseGeneration, backgroundConsentId, confirmed: true }); valid(version);
          const background = await reader.backgroundStatus(); valid(version);
          if (!background.enabled || background.consentId !== backgroundConsentId || background.scopeConsentId !== local.consentId
            || background.databaseGeneration !== local.databaseGeneration) throw new Error("Android did not activate the approved background SMS scope.");
          update({ background, remote: { ...remote, backgroundSync: grant }, message: "Background SMS sync is enabled for the approved direct conversations. Android schedules the work; delivery to EPIC may be delayed." });
        } catch (error) {
          if (generation === version) {
            await reader.backgroundStop(); valid(version);
            const background = await reader.backgroundStatus(); valid(version);
            update({ background });
            await stopRemoteBackground(local, local.consentId, backgroundConsentId, version, signal);
            const stopped = await reader.backgroundStatus(); valid(version);
            update({ background: stopped });
          }
          throw error;
        }
      });
    },
    clearReply() { if (!state.busy) update({ reply: null, message: "", error: false }); },
    sendReply(conversationRef: string, text: string, idempotencyKey: string) {
      return operation(async (version, signal) => {
        if (!text.trim() || text.length > 1000) throw new Error("Enter a reply of up to 1000 characters.");
        const { local, remote } = await reconcile(version, signal); valid(version);
        if (!state.enabled || !remote.sendEnabled) throw new Error("Enable phone SMS access and server replies before sending.");
        if (remote.conversationRef !== conversationRef && !remote.conversations?.some(item => item.conversationRef === conversationRef)) throw new Error("This conversation is not approved for this phone.");
        const permit = await post<SmsReplyPermit>("reply-authorize", local, { consentId: local.consentId, conversationRef, text, idempotencyKey }, signal); valid(version);
        if (permit.ok !== true || permit.account !== state.account || permit.consentId !== local.consentId
            || permit.conversationRef !== conversationRef || (local.mode !== "all" && local.mode !== "selected" && permit.phoneNumber !== local.phoneNumber) || permit.text !== text
            || !/^androidsms_send_[a-f0-9]{64}$/.test(permit.operationId) || !Number.isSafeInteger(permit.expiresAt)
            || permit.expiresAt <= Date.now() || permit.expiresAt > Date.now() + 120000) throw new Error("The reply authorization did not match this phone and conversation.");
        update({ message: "Confirm this SMS on your phone. Carrier charges may apply." });
        const result = await reader.send(permit); valid(version);
        const reply = { ...result, operationId: permit.operationId };
        update({ reply, message: replyMessage(reply.status) });
      });
    },
    checkReply() {
      return operation(async (version) => {
        const id = state.reply?.operationId;
        if (!id) return;
        const result = await reader.sendStatus({ operationId: id }); valid(version);
        update({ reply: { ...result, operationId: id }, message: replyMessage(result.status) });
      });
    },
    enable(selection: SmsSelection, confirmed: boolean) {
      return operation(async (version, signal) => {
        if (!confirmed) throw new Error("Approve the account and conversation before enabling sync.");
        const { local, remote } = await reconcile(version, signal); valid(version);
        if (!remote.enabled) throw new Error("Phone SMS sync is not enabled on the server yet.");
        if (remote.status === "active") {
          await reader.stop({ mode: "pause" }); valid(version);
          const paused = await post<SmsRemote>("pause", local, { consentId: remote.consentId }, signal); valid(version);
          remote.revision = paused.revision;
          await reader.stopAcknowledged({ consentId: local.consentId, mode: "pause" }); valid(version);
        }
        const consentId = crypto.randomUUID();
        const multi = selection.mode === "all" || selection.mode === "selected";
        if (multi && !remote.supportedConsentVersions?.includes("android-sms.v2")) throw new Error("The server update for multiple SMS conversations is required.");
        await reader.approve({ ...selection, account: state.account, consentId, confirmed }); valid(version);
        const approved = await reader.status(); valid(version);
        const approvedScope = multi ? { mode: selection.mode, selections: selection.selections || [], country: selection.country,
          since: selection.since, databaseGeneration: approved.databaseGeneration, initialUpper: approved.initialUpper }
          : { phoneNumber: selection.phoneNumber, country: selection.country, since: selection.since };
        const result = await post<SmsRemote>("register", local, { scope: approvedScope, consentVersion: multi ? "android-sms.v2" : "android-sms.v1", consentId,
          previousConsentId: remote.consentId, previousRevision: remote.revision, confirmed }, signal); valid(version);
        if (result.status !== "active" || result.consentId !== consentId) throw new Error("SMS approval is no longer active.");
        await reader.activate({ consentId }); valid(version);
        await reconcile(version, signal);
        update({ message: "SMS sync enabled. No messages have been read yet." });
      });
    },
    sync(rescan = false) {
      return operation(async (version, signal) => {
        const { local } = await reconcile(version, signal); valid(version);
        if (!state.enabled) throw new Error(state.message || "Enable SMS sync and allow Android SMS access first.");
        if (local.background?.enabled) {
          await reader.backgroundSync({ rescan }); valid(version);
          const background = await reader.backgroundStatus(); valid(version);
          update({ background, message: rescan ? "History rescan scheduled on Android within your approved scope." : "SMS sync scheduled on Android. You can close EPIC; Android may delay the work." });
          return;
        }
        update({ syncing: true });
        await reader.begin({ rescan }); valid(version);
        let imported = 0;
        for (let pageNumber = 0; pageNumber < 100; pageNumber++) {
          const page = await reader.readPage(); valid(version);
          const authority = await reader.status(); valid(version);
          if (!authority.enabled || !authority.permissionGranted || authority.account !== local.account || authority.consentId !== local.consentId) {
            await reader.stop({ mode: "pause" }); valid(version);
            await post<SmsRemote>("pause", local, { consentId: local.consentId }, signal); valid(version);
            throw new Error("SMS permission or account authority changed. Phone sync is paused.");
          }
          const result = await post<{ ok: boolean; accepted: number; imported: number; consentId: string; pageId?: string; databaseGeneration?: string; acknowledgedThrough?: number; coverage?: { state: string; reasonCode: string; unavailableCount?: number } }>("upload", local,
            { consentId: local.consentId, messages: page.messages, ...(local.mode === "all" || local.mode === "selected" ? { databaseGeneration: page.databaseGeneration, page: page.page } : {}) }, signal); valid(version);
          if (result.ok !== true || result.accepted !== page.messages.length || result.consentId !== local.consentId) throw new Error("The server did not acknowledge this SMS page. Retry the sync.");
          if ((local.mode === "all" || local.mode === "selected") && (result.pageId !== page.pageId || result.databaseGeneration !== local.databaseGeneration
              || result.acknowledgedThrough !== page.page?.through)) throw new Error("The server did not acknowledge this exact SMS checkpoint. Retry the sync.");
          await reader.acknowledge({ pageId: page.pageId }); valid(version);
          imported += result.imported;
          update({ message: `${imported} new messages imported.` });
          if (!page.hasMore) {
            await reconcile(version, signal);
            const coverageNote = result.coverage?.state === "complete_within_scope" ? "Device snapshot complete within the approved SMS scope."
              : result.coverage?.reasonCode === "SMS_LEGACY_THREAD_SCOPE_UNAVAILABLE" ? "Sync finished with partial coverage. Earlier phone-only imports remain in their original Inbox thread because their SIM and thread scope is unknown."
              : result.coverage?.reasonCode === "SMS_SOURCE_SCOPE_UNAVAILABLE" ? "Sync finished with partial coverage. Some source addresses or thread participants are unsupported, or source rows disappeared during the scan."
              : "The accepted SMS scan finished. Source coverage is not fully verified.";
            update({ message: `${coverageNote} ${imported} new records imported. This covers sent and received SMS in direct threads at the scan snapshot. RCS, MMS, group threads, pending/failed sends, and deleted device history are unavailable.` });
            return;
          }
        }
        update({ message: `${imported} new messages imported. Tap Sync now to continue the remaining history.` });
      });
    },
    async stop(mode: "pause" | "revoke") {
      generation++; request?.abort(); request = null;
      const version = generation;
      update({ enabled: false, syncing: false, busy: true, pendingStop: true, background: state.background ? { ...state.background, enabled: false, state: "paused" } : null, error: false, message: "Stopping SMS sync..." });
      try {
        await reader.stop({ mode }); valid(version);
        update({ message: "Phone reads stopped. Confirming with the server..." });
        await reconcile(version); valid(version);
        update({ message: mode === "revoke" ? "SMS access revoked in EPIC. Imported messages remain in your Inbox." : "SMS sync stopped. Imported messages remain in your Inbox." });
      } catch (_) {
        if (generation === version) update({ error: true, message: "Sync is stopped locally. Reconnect and retry to confirm the server stop. You can also remove SMS permission in Android settings." });
      } finally { if (generation === version) update({ busy: false }); }
    },
    suspend() {
      if (!state.syncing) return;
      generation++; request?.abort(); request = null;
      void reader.cancel().catch(() => {});
      update({ busy: false, syncing: false, message: "Sync paused while the app is in the background. Tap Sync now to continue." });
    }
  };
}

function readinessMessage(remote: SmsRemote) {
  const code = remote.readiness?.reasonCode;
  if (code === "SMS_DATA_KEY_REQUIRED" || code === "SMS_DATA_KEY_INVALID") return "SMS server security setup is incomplete. No phone messages will be read.";
  if (code && code !== "SMS_SYNC_DISABLED") return "SMS server storage is not ready. No phone messages will be read.";
  return "Phone SMS sync is not enabled on the server yet.";
}

export function smsBackgroundHealth(background: SmsBackgroundStatus | null, now = Date.now()) {
  if (!background) return "Background sync is off. Review the approved direct-conversation scope before enabling it.";
  const attempted = background.lastAttemptAt == null ? NaN : new Date(background.lastAttemptAt).getTime();
  const stale = background.enabled && (background.state === "syncing" && (!Number.isFinite(attempted) || now - attempted > 15 * 60 * 1000)
    || Number.isFinite(attempted) && now - attempted > 45 * 60 * 1000);
  const messages: Record<string, string> = {
    needs_login: "Needs login. Sign in to the bound account, then review and enable background sync again.",
    failed: "Background sync failed. Recheck status and Android permissions before retrying.",
    paused: "Background sync is paused on this phone.",
    syncing: "Background sync is running on Android.",
    retrying: "Background sync is waiting to retry. Recheck status for current progress.",
    scheduled: "Background sync is scheduled. Android may delay the next run.",
    delayed: "No recent background progress is confirmed. Android scheduling or connectivity may delay work; recheck status.",
    disabled: "Background sync is off.",
    up_to_date: "The last background scan completed within the approved scope. Android will schedule new work."
  };
  const knownFailure = background.state === "failed" || background.state === "needs_login";
  const retryReasons: Record<string, string> = {
    SMS_PENDING_TRACKING_LIMIT: "Pending outgoing-message checks exceed the local tracking window. Background sync is continuing recovery across the approved history scope.",
    SMS_SYNC_MORE_HISTORY: "Background sync has more approved history to process. Android will schedule another run.",
    SMS_READER_BUSY: "The SMS reader is in use. Background sync is waiting to retry.",
    SMS_NETWORK_UNAVAILABLE: "Background sync is waiting for connectivity to retry."
  };
  const reason = background.state === "retrying" ? retryReasons[background.reasonCode || ""] : null;
  const message = stale && !knownFailure ? messages.delayed : reason || messages[background.state] || (background.enabled ? "Background sync is enabled and waiting for Android to schedule work." : "Background sync is paused on this phone.");
  const partial = ["SMS_SOURCE_SCOPE_UNAVAILABLE", "SMS_SOURCE_ADDRESSES_UNAVAILABLE", "SMS_LEGACY_THREAD_SCOPE_UNAVAILABLE"].includes(background.reasonCode || "");
  return `${message}${stale && knownFailure ? ` ${messages.delayed}` : ""}${partial ? " Source coverage is partial: some SMS records or direct-thread participant details are unavailable." : ""}${background.pendingStop ? " Server stop confirmation is pending. Reconnect and recheck status." : ""}`;
}

export function replyMessage(status: SmsReplyResult["status"]) {
  if (status === "sent") return "Sent by your phone. Delivery is not confirmed. Sync SMS to update the EPIC Inbox.";
  if (status === "cancelled" || status === "not_sent") return "Nothing was sent.";
  if (status === "failed") return "Your phone reported that sending failed. Check your messaging app before trying again.";
  if (status === "partial") return "Only part of this SMS was sent. Check your messaging app before trying again.";
  if (status === "unknown") return "The send result is unknown. Check your messaging app; EPIC will not resend automatically.";
  return "Submitted to your phone. Sending is not confirmed yet.";
}
